import React, { ChangeEvent, memo, useEffect, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { ko } from 'date-fns/locale/ko';
import { getYear, getMonth } from 'date-fns';
import { getYearOption } from '../../utils/common';
import arrowDownLine from '@/assets/icons/arrowDownLine.svg';
import theme from '@styles/theme';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';

type StyledCalendarProps = {
	row?: boolean;
	arrow?: boolean;
	title?: string;
	width?: number;
	margin?: string;
	dateFormat?: string;
	value: Date | null;
	onChange: (date: Date | null) => void;
	minDate?: Date | null;
	maxDate?: Date | null;
	excludeDates?: Date[];
	placeholder?: string;
	disabled?: boolean;
	yearOption?: string[] | number[];
};

const MONTHS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'] as const;

const CustomHeader = memo(
	({
		date,
		changeYear,
		changeMonth,
		decreaseMonth,
		increaseMonth,
		yearOption,
	}: ReactDatePickerCustomHeaderProps & { yearOption?: string[] | number[] }) => {
		const [years, setYears] = useState(getYearOption(2020));

		useEffect(() => {
			if (yearOption) {
				setYears(yearOption);
			}
		}, [yearOption]);

		const handleYearChange = useCallback(
			(e: ChangeEvent<HTMLSelectElement>) => {
				changeYear(parseInt(e.target.value, 10));
			},
			[changeYear],
		);

		const handleMonthChange = useCallback(
			(e: ChangeEvent<HTMLSelectElement>) => {
				changeMonth(MONTHS.indexOf(e.target.value as (typeof MONTHS)[number]));
			},
			[changeMonth],
		);

		return (
			<Header>
				<ArrowBox onClick={decreaseMonth}>
					<RiArrowLeftSLine fontSize={22} color={theme.colors.whiteColor} />
				</ArrowBox>
				<SelectBox value={getYear(date)} onChange={handleYearChange} aria-label="연도 선택">
					{years?.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</SelectBox>
				<SelectBox value={MONTHS[getMonth(date)]} onChange={handleMonthChange} aria-label="월 선택">
					{MONTHS.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</SelectBox>
				<ArrowBox onClick={increaseMonth}>
					<RiArrowRightSLine fontSize={22} color={theme.colors.whiteColor} />
				</ArrowBox>
			</Header>
		);
	},
);

const StyledCalendar = ({
	row,
	arrow = true,
	title,
	width,
	margin,
	dateFormat = 'yyyy-MM-dd',
	value = new Date(),
	onChange,
	excludeDates = [],
	minDate,
	maxDate,
	placeholder,
	disabled,
	yearOption,
}: StyledCalendarProps) => {
	const widthValue = typeof width === 'number' ? width : undefined;

	const renderCustomHeader = useCallback(
		(props: ReactDatePickerCustomHeaderProps) => <CustomHeader {...props} yearOption={yearOption} />,
		[yearOption],
	);

	if (row) {
		return (
			<Wrapper $row $width={widthValue} $margin={margin}>
				{title && <RowTitle>{title}</RowTitle>}
				<DateBox $row $arrow={arrow}>
					<DatePicker
						dateFormat={dateFormat}
						locale={ko}
						selected={value}
						onChange={onChange}
						excludeDates={excludeDates}
						minDate={minDate ?? undefined}
						maxDate={maxDate ?? undefined}
						disabled={disabled}
						renderCustomHeader={renderCustomHeader}
						// includeDates={includeDates.map(date => new Date(date))}
						aria-label={title || '날짜 선택'}
						showMonthYearDropdown
					/>
				</DateBox>
			</Wrapper>
		);
	}

	return (
		<Wrapper $row={row} $width={widthValue} $margin={margin}>
			{title && <ColumnTitle>{title}</ColumnTitle>}
			<DateBox $arrow={arrow} $row={row}>
				<DatePicker
					dateFormat={dateFormat}
					locale={ko}
					selected={value}
					onChange={onChange}
					excludeDates={excludeDates}
					minDate={minDate ?? undefined}
					maxDate={maxDate ?? undefined}
					placeholderText={placeholder}
					disabled={disabled}
					renderCustomHeader={renderCustomHeader}
					aria-label={title || placeholder || '날짜 선택'}
				/>
			</DateBox>
		</Wrapper>
	);
};

CustomHeader.displayName = 'CustomHeader';
StyledCalendar.displayName = 'StyledCalendar';

export default memo(StyledCalendar);

const Wrapper = styled.div<{ $row?: boolean; $width?: number; $margin?: string }>`
	width: ${({ $width }) => ($width ? `${$width}px` : '305px')};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin: ${({ $margin }) => ($margin ? $margin : 0)};

	${({ $row }) =>
		$row &&
		css`
			flex-direction: row;
			align-items: center;
		`};
`;
const ColumnTitle = styled.div`
	font-size: 14px;
	margin-bottom: 12px;
`;
const RowTitle = styled.div`
	font-size: 14px;
	margin-right: 12px;
`;
const DateBox = styled.div<{ $row?: boolean; $arrow?: boolean; $margin?: string }>`
	width: 100%;
	height: 50px;
	position: relative;
	border-radius: 4px;
	border: 1px solid ${theme.colors.ultraLightGrayBorder};
	background-position: top 50% right 12px;

	.react-datepicker-wrapper,
	.react-datepicker__input-container {
		width: 100%;
		height: 100%;
	}

	.react-datepicker {
		transform: translateX(-13px);
		border: none;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
	}

	.react-datepicker-popper .react-datepicker__triangle {
		stroke: ${theme.colors.blackColor};
	}

	.react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
		fill: black;
		color: black;
	}

	.react-datepicker__header {
		padding: 12px 0 8px;
		border-bottom: none;
		background-color: ${theme.colors.blackColor};
	}

	.react-datepicker__header:not(.react-datepicker__header--has-time-select) {
		border-top-right-radius: 0.2rem;
		border-top-left-radius: 0.2rem;
	}

	.react-datepicker__day-name {
		//color: ${theme.colors.whiteColor};
	}

	.react-datepicker__day--selected {
		background-color: ${theme.colors.blackColor};
	}

	.react-datepicker__year-read-view--down-arrow,
	.react-datepicker__month-read-view--down-arrow,
	.react-datepicker__month-year-read-view--down-arrow,
	.react-datepicker__navigation-icon::before {
		border-color: ${theme.colors.whiteColor};
	}

	input {
		width: 100%;
		height: 100%;
		padding: 0 12px;
		cursor: pointer;

		&::placeholder {
			color: ${theme.colors.deepGrayFontColor};
		}
	}

	${({ $arrow }) =>
		$arrow &&
		css`
			background-image: url(${arrowDownLine});
			background-size: 16px;
			background-repeat: no-repeat;
			background-position: top 50% right 12px;
		`};

	${({ $row }) =>
		$row &&
		css`
			height: auto;
			border: none;
		`};
`;
const ArrowBox = styled.div`
	display: flex;
	cursor: pointer;
`;
const Header = styled.div`
	width: 100%;
	height: 30px;
	padding: 0 6px;
	display: flex;
	align-items: center;
	justify-content: space-around;
`;
const SelectBox = styled.select`
	width: 68px;
	height: 26px;
	padding: 0 6px;
	text-align: left;
	border: none;
	border-radius: 4px;
	background-image: url(${arrowDownLine});
	background-size: 12px;
	background-repeat: no-repeat;
	background-position: right 6px top 52%;

	&:nth-child(1) {
		width: 48px;
	}
`;
